import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useTranslation } from '../hooks/useTranslation';
import { useClaudeWS } from '../hooks/useClaudeWS';
import ClaudeMessageBlocks from '../components/ClaudeMessageBlocks';

const MAX_FILES = 10;
const MAX_FILE_SIZE = 50 * 1024 * 1024;

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function AdminClaude({ token, backendUrl }) {
  const { t } = useTranslation();

  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [activeConv, setActiveConv] = useState(null);

  const [instruction, setInstruction] = useState('');
  const [mode, setMode] = useState('query');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 上傳檔案：pending = 已上傳到後端但還沒送 instruction
  const [pendingFiles, setPendingFiles] = useState([]); // [{file_id, filename, size}]
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const loadConversations = useCallback(async (signal) => {
    try {
      const res = await axios.get(
        `${backendUrl}/users/admin/claude/conversations`,
        { headers: { Authorization: `Bearer ${token}` }, signal }
      );
      setConversations(res.data.conversations || []);
    } catch (err) {
      if (axios.isCancel(err) || err.name === 'CanceledError') return;
      console.error('Failed to load conversations', err);
    }
  }, [backendUrl, token]);

  useEffect(() => {
    const controller = new AbortController();
    loadConversations(controller.signal);
    return () => controller.abort();
  }, [loadConversations]);

  useEffect(() => {
    if (!activeConvId) {
      setMessages([]);
      setActiveConv(null);
      return;
    }
    const controller = new AbortController();
    (async () => {
      try {
        const res = await axios.get(
          `${backendUrl}/users/admin/claude/conversations/${activeConvId}`,
          { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal }
        );
        setMessages(res.data.messages || []);
        setActiveConv(res.data.conversation || null);
        if (res.data.conversation?.mode) {
          setMode(res.data.conversation.mode);
        }
      } catch (err) {
        if (axios.isCancel(err) || err.name === 'CanceledError') return;
        console.error('Failed to load conversation', err);
      }
    })();
    return () => controller.abort();
  }, [activeConvId, backendUrl, token]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const updateLastAssistant = useCallback((updater) => {
    setMessages((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last && last.role === 'assistant') {
        next[next.length - 1] = updater(last);
      }
      return next;
    });
  }, []);

  // 把單筆 stream-json event 整理成 blocks，append 到末 assistant
  const handleClaudeEvent = useCallback((sj) => {
    if (!sj || !sj.type) return;
    if (sj.type === 'system' && sj.subtype === 'init') {
      updateLastAssistant((last) => ({
        ...last,
        meta: { ...(last.meta || {}), session_id: sj.session_id, model: sj.model },
      }));
      return;
    }
    if (sj.type === 'result') {
      updateLastAssistant((last) => ({
        ...last,
        meta: {
          ...(last.meta || {}),
          session_id: sj.session_id || last.meta?.session_id,
          duration_ms: sj.duration_ms,
          num_turns: sj.num_turns,
          total_cost_usd: sj.total_cost_usd,
          subtype: sj.subtype,
          is_error: sj.is_error,
        },
        content: sj.result || last.content,
      }));
      return;
    }
    if (sj.type === 'assistant') {
      const contents = sj.message?.content || [];
      updateLastAssistant((last) => {
        const blocks = [...(last.blocks || [])];
        for (const c of contents) {
          if (c.type === 'text') {
            const lastBlock = blocks[blocks.length - 1];
            if (lastBlock?.type === 'text') {
              // 合併連續 text，避免每次 chunk 都新增一塊
              blocks[blocks.length - 1] = { ...lastBlock, text: lastBlock.text + (c.text || '') };
            } else {
              blocks.push({ type: 'text', text: c.text || '' });
            }
          } else if (c.type === 'tool_use') {
            blocks.push({ type: 'tool_use', id: c.id, name: c.name, input: c.input });
          } else if (c.type === 'thinking') {
            blocks.push({ type: 'thinking', thinking: c.thinking || '' });
          }
        }
        return { ...last, blocks };
      });
      return;
    }
    if (sj.type === 'user') {
      const contents = sj.message?.content || [];
      updateLastAssistant((last) => {
        const blocks = [...(last.blocks || [])];
        for (const c of contents) {
          if (c.type === 'tool_result') {
            blocks.push({
              type: 'tool_result',
              tool_use_id: c.tool_use_id,
              content: c.content,
              is_error: !!c.is_error,
            });
          }
        }
        return { ...last, blocks };
      });
    }
  }, [updateLastAssistant]);

  const handleWSEvent = useCallback((frame) => {
    if (!frame?.type) return;
    switch (frame.type) {
      case 'ready':
        // 連線建立，不動 UI
        return;
      case 'reconnecting':
        setError(`連線中斷，第 ${frame.attempt} 次重連...`);
        return;
      case 'pong':
        return;
      case 'user_message_saved':
        // 後端已寫 user msg，前端用 optimistic 已有顯示，這裡不重複追加
        return;
      case 'queued':
        updateLastAssistant((last) => ({
          ...last,
          streaming: true,
          queued: true,
          queuePosition: frame.position,
        }));
        return;
      case 'running':
        updateLastAssistant((last) => ({ ...last, queued: false, queuePosition: undefined }));
        return;
      case 'claude_event':
        handleClaudeEvent(frame.event);
        return;
      case 'info':
        updateLastAssistant((last) => ({
          ...last,
          blocks: [...(last.blocks || []), { type: 'text', text: `[${frame.data}]` }],
        }));
        return;
      case 'done':
        updateLastAssistant((last) => ({
          ...last,
          streaming: false,
          queued: false,
          meta: {
            ...(last.meta || {}),
            exit_code: frame.exit_code,
            ...(frame.rebuild ? { rebuild: frame.rebuild } : {}),
          },
        }));
        setLoading(false);
        loadConversations();
        return;
      case 'error':
        setError(frame.data || '未知錯誤');
        updateLastAssistant((last) => ({
          ...last,
          streaming: false,
          queued: false,
          meta: { ...(last.meta || {}), interrupted: true },
        }));
        setLoading(false);
        return;
      default:
        return;
    }
  }, [handleClaudeEvent, updateLastAssistant, loadConversations]);

  const wsEnabled = !!activeConvId;
  const { status: wsStatus, send: wsSend } = useClaudeWS({
    backendUrl,
    conversationId: activeConvId,
    enabled: wsEnabled,
    onEvent: handleWSEvent,
  });

  const handleNewConversation = async () => {
    try {
      const res = await axios.post(
        `${backendUrl}/users/admin/claude/conversations`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newConv = res.data;
      setConversations((prev) => [newConv, ...prev]);
      setActiveConvId(newConv.conversation_id);
      setMessages([]);
      setActiveConv(newConv);
      setError(null);
      setInstruction('');
      setPendingFiles([]);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleDeleteConversation = async (convId, e) => {
    e.stopPropagation();
    if (!window.confirm(t('claude.confirmDelete'))) return;
    try {
      await axios.delete(
        `${backendUrl}/users/admin/claude/conversations/${convId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConversations((prev) => prev.filter((c) => c.conversation_id !== convId));
      if (activeConvId === convId) {
        setActiveConvId(null);
        setMessages([]);
        setActiveConv(null);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleFiles = async (fileList) => {
    const incoming = Array.from(fileList || []);
    if (incoming.length === 0) return;
    if (pendingFiles.length + incoming.length > MAX_FILES) {
      setError(`最多 ${MAX_FILES} 個檔案`);
      return;
    }
    const oversize = incoming.find((f) => f.size > MAX_FILE_SIZE);
    if (oversize) {
      setError(`檔案 ${oversize.name} 超過 50 MB 上限`);
      return;
    }

    const fd = new FormData();
    incoming.forEach((f) => fd.append('files', f));
    setUploading(true);
    setError(null);
    try {
      const res = await axios.post(
        `${backendUrl}/users/admin/claude/files`,
        fd,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newFiles = res.data?.files || [];
      setPendingFiles((prev) => [...prev, ...newFiles]);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removePendingFile = async (fileId) => {
    try {
      await axios.delete(
        `${backendUrl}/users/admin/claude/files/${fileId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (_) {
      /* ignore */
    }
    setPendingFiles((prev) => prev.filter((f) => f.file_id !== fileId));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!instruction.trim() || loading) return;

    let convId = activeConvId;
    if (!convId) {
      try {
        const res = await axios.post(
          `${backendUrl}/users/admin/claude/conversations`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const newConv = res.data;
        setConversations((prev) => [newConv, ...prev]);
        setActiveConvId(newConv.conversation_id);
        setActiveConv(newConv);
        convId = newConv.conversation_id;
        // 等下次 effect 連 WS：不能在這裡立即 send
        setError('正在建立對話與連線，請稍候片刻再送出');
        return;
      } catch (err) {
        setError(err.response?.data?.detail || err.message);
        return;
      }
    }

    if (wsStatus !== 'open') {
      setError(`WebSocket 尚未就緒（${wsStatus}），請稍後再試`);
      return;
    }

    const userInstruction = instruction.trim();
    const fileIds = pendingFiles.map((f) => f.file_id);
    const filesSnapshot = pendingFiles;
    setInstruction('');
    setPendingFiles([]);
    setLoading(true);
    setError(null);

    const now = new Date().toISOString();
    setMessages((prev) => [
      ...prev,
      {
        conversation_id: convId,
        role: 'user',
        content: userInstruction,
        mode,
        blocks: [{ type: 'text', text: userInstruction }],
        file_ids: fileIds.length ? fileIds : undefined,
        files: filesSnapshot,  // 前端 only：給渲染用
        created_at: now,
      },
      {
        conversation_id: convId,
        role: 'assistant',
        content: '',
        mode,
        blocks: [],
        meta: {},
        created_at: now,
        streaming: true,
      },
    ]);

    const ok = wsSend({
      type: 'instruction',
      instruction: userInstruction,
      mode,
      file_ids: fileIds,
    });
    if (!ok) {
      setLoading(false);
      setError('WebSocket 傳送失敗');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleString('zh-TW', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const wsBadge = useMemo(() => {
    if (!activeConvId) return null;
    const map = {
      idle: { text: '未連線', cls: 'bg-secondary' },
      connecting: { text: '連線中', cls: 'bg-info' },
      open: { text: '已連線', cls: 'bg-success' },
      closed: { text: '已斷線', cls: 'bg-warning text-dark' },
      error: { text: '連線錯誤', cls: 'bg-danger' },
    };
    const v = map[wsStatus] || map.idle;
    return <span className={`badge ${v.cls} ms-2`} style={{ fontSize: '0.65rem' }}>{v.text}</span>;
  }, [wsStatus, activeConvId]);

  return (
    <div className="d-flex" style={{ height: 'calc(100vh - 80px)' }}>
      <div className="border-end bg-body-tertiary d-flex flex-column" style={{ width: '280px', minWidth: '280px' }}>
        <div className="p-3 border-bottom">
          <button className="btn btn-primary w-100" onClick={handleNewConversation} disabled={loading}>
            <i className="bi bi-plus-lg me-2" aria-hidden="true"></i>{t('claude.newConversation')}
          </button>
        </div>
        <div className="flex-grow-1 overflow-auto">
          {conversations.length === 0 ? (
            <div className="text-muted text-center p-3" style={{ fontSize: '0.85rem' }}>
              {t('claude.noConversations')}
            </div>
          ) : (
            conversations.map((conv) => (
              <div
                key={conv.conversation_id}
                className={`d-flex align-items-center px-3 py-2 border-bottom cursor-pointer ${
                  activeConvId === conv.conversation_id ? 'bg-body-secondary border-start border-primary border-3' : ''
                }`}
                style={{ cursor: 'pointer' }}
                onClick={() => setActiveConvId(conv.conversation_id)}
              >
                <div className="flex-grow-1 text-truncate">
                  <div className="text-truncate fw-medium" style={{ fontSize: '0.9rem' }}>
                    {conv.title || t('claude.newConversation')}
                  </div>
                  <small className="text-muted">{formatTime(conv.updated_at)}</small>
                </div>
                <button
                  className="btn btn-sm btn-link text-danger p-0 ms-2"
                  onClick={(e) => handleDeleteConversation(conv.conversation_id, e)}
                  title={t('claude.deleteConversation')}
                  aria-label={t('claude.deleteConversation')}
                >
                  <i className="bi bi-trash" aria-hidden="true"></i>
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="flex-grow-1 d-flex flex-column">
        <div className="p-3 border-bottom d-flex align-items-center">
          <h5 className="mb-0 me-3">
            <i className="bi bi-robot me-2" aria-hidden="true"></i>
            {activeConv?.title || t('claude.aiAssistant')}
            {wsBadge}
          </h5>
          <div className="btn-group btn-group-sm ms-auto">
            <button
              type="button"
              className={`btn ${mode === 'query' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setMode('query')}
              disabled={loading}
            >
              <i className="bi bi-search me-1" aria-hidden="true"></i>{t('claude.dataQuery')}
            </button>
            <button
              type="button"
              className={`btn ${mode === 'frontend' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setMode('frontend')}
              disabled={loading}
            >
              <i className="bi bi-code-slash me-1" aria-hidden="true"></i>{t('claude.frontendEdit')}
            </button>
          </div>
        </div>

        <div className="flex-grow-1 overflow-auto p-3 bg-body-tertiary">
          {!activeConvId && messages.length === 0 ? (
            <div className="text-center text-muted mt-5">
              <i className="bi bi-chat-dots" style={{ fontSize: '3rem' }} aria-hidden="true"></i>
              <p className="mt-3">{t('claude.startHint')}</p>
            </div>
          ) : (
            messages.map((msg, idx) => (
              <div
                key={idx}
                className={`d-flex mb-3 ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
              >
                <div
                  className={`rounded-3 p-3 ${msg.role === 'user' ? 'bg-primary text-white' : 'bg-body border'}`}
                  style={{ maxWidth: msg.role === 'assistant' ? '90%' : '80%', minWidth: '100px' }}
                >
                  {msg.role === 'assistant' ? (
                    msg.blocks?.length ? (
                      <ClaudeMessageBlocks
                        blocks={msg.blocks}
                        meta={msg.meta}
                        streaming={msg.streaming}
                      />
                    ) : (
                      // 舊資料 / 尚未收到任何 block：fallback 顯示 content
                      <pre
                        className="mb-0"
                        style={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          fontSize: '0.85rem',
                          fontFamily: 'inherit',
                        }}
                      >
                        {msg.content || ''}
                        {msg.streaming && (
                          <span className="text-muted" style={{ animation: 'blink 1s infinite' }}>▋</span>
                        )}
                        {msg.queued && msg.queuePosition >= 0 && (
                          <span className="text-info ms-2" style={{ fontSize: '0.75rem' }}>
                            排隊中（位置 {msg.queuePosition + 1}）
                          </span>
                        )}
                      </pre>
                    )
                  ) : (
                    <>
                      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {msg.content}
                      </div>
                      {(msg.files?.length || msg.file_ids?.length) > 0 && (
                        <div className="mt-2 d-flex flex-wrap gap-1">
                          {(msg.files || msg.file_ids.map((id) => ({ file_id: id, filename: id.slice(0, 8) }))).map((f) => (
                            <span
                              key={f.file_id}
                              className="badge bg-light text-dark border"
                              style={{ fontSize: '0.7rem' }}
                              title={f.filename}
                            >
                              <i className="bi bi-paperclip me-1" aria-hidden="true"></i>
                              {f.filename}{f.size != null ? ` (${formatBytes(f.size)})` : ''}
                            </span>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  <div className={`mt-1 ${msg.role === 'user' ? 'text-white-50' : 'text-muted'}`} style={{ fontSize: '0.75rem' }}>
                    {formatTime(msg.created_at)}
                    {msg.role === 'assistant' && msg.queued && msg.queuePosition >= 0 && (
                      <span className="ms-2 text-info">
                        <i className="bi bi-hourglass-split me-1" aria-hidden="true"></i>
                        排隊中（位置 {msg.queuePosition + 1}）
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {error && (
          <div className="alert alert-danger m-3 mb-0 py-2 d-flex align-items-center">
            <i className="bi bi-exclamation-triangle me-2" aria-hidden="true"></i>
            <span className="flex-grow-1">{error}</span>
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label="關閉錯誤"
            ></button>
          </div>
        )}

        <div
          className={`p-3 border-top bg-body ${dragOver ? 'border-primary border-3' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          {pendingFiles.length > 0 && (
            <div className="mb-2 d-flex flex-wrap gap-2">
              {pendingFiles.map((f) => (
                <span key={f.file_id} className="badge bg-secondary d-inline-flex align-items-center" style={{ fontSize: '0.75rem' }}>
                  <i className="bi bi-paperclip me-1" aria-hidden="true"></i>
                  {f.filename} ({formatBytes(f.size)})
                  <button
                    type="button"
                    className="btn-close btn-close-white ms-2"
                    style={{ fontSize: '0.6rem' }}
                    onClick={() => removePendingFile(f.file_id)}
                    aria-label="移除檔案"
                  ></button>
                </span>
              ))}
            </div>
          )}
          <form onSubmit={handleSubmit} className="d-flex gap-2 align-items-end">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={(e) => handleFiles(e.target.files)}
              aria-label="選擇檔案"
            />
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading || uploading || pendingFiles.length >= MAX_FILES}
              title={`附件（${pendingFiles.length}/${MAX_FILES}，單檔 ≤ 50 MB）`}
              aria-label="上傳檔案"
            >
              {uploading ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              ) : (
                <i className="bi bi-paperclip" aria-hidden="true"></i>
              )}
            </button>
            <textarea
              ref={textareaRef}
              className="form-control"
              rows={2}
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={mode === 'query' ? t('claude.queryPlaceholder') : t('claude.frontendPlaceholder')}
              disabled={loading}
              style={{ resize: 'none' }}
              aria-label={mode === 'query' ? t('claude.queryPlaceholder') : t('claude.frontendPlaceholder')}
            />
            <button
              type="submit"
              className="btn btn-primary align-self-end"
              disabled={loading || !instruction.trim()}
              style={{ minWidth: '80px' }}
              aria-label={t('claude.ctrlEnterHint')}
            >
              {loading ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              ) : (
                <i className="bi bi-send" aria-hidden="true"></i>
              )}
            </button>
          </form>
          <small className="text-muted mt-1 d-block">
            {t('claude.ctrlEnterHint')}
            <span className="ms-2">· 拖檔或按 <i className="bi bi-paperclip" aria-hidden="true"></i> 上傳（{pendingFiles.length}/{MAX_FILES}）</span>
          </small>
        </div>
      </div>
    </div>
  );
}

export default AdminClaude;
